import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Bell, Lightbulb } from 'lucide-react';
import { SmartNudgesBanner, SmartNudgesWidget, SmartNudgesNotification } from '@/components/SmartNudges';

/**
 * Smart Nudges Demo Page
 * Shows all nudge variants in action
 */

const SmartNudgesDemo = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 pb-20">
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center mb-3">
            <div className="bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full p-3">
              <Bell className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">
            Smart Nudges System
          </h1>
          <p className="text-gray-600">
            Gentle, behavior-based suggestions
          </p>
        </div>

        {/* Info Card */}
        <Card className="border-2 border-blue-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lightbulb className="h-6 w-6 text-blue-600" />
              How Smart Nudges Work
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm text-gray-700">
              <p>
                <strong>Smart Detection:</strong> The system analyzes your daily habits and identifies 
                gaps (like not logging water, low sleep, or missed activities).
              </p>
              <p>
                <strong>Gentle Timing:</strong> Nudges appear at appropriate times (not during sleep hours) 
                and respect frequency limits to avoid being annoying.
              </p>
              <p>
                <strong>Supportive Tone:</strong> All messages use caring, non-judgmental language like 
                "You might try..." instead of "You must..."
              </p>
              <p>
                <strong>Smart Limits:</strong> Maximum 2-3 nudges per day, with hours between each type 
                to prevent notification fatigue.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Banner Variant Demo */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-3">
            Banner Style (Top of Page)
          </h2>
          <SmartNudgesBanner />
        </div>

        {/* Widget Variant Demo */}
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              Widget Style (Dashboard)
            </h2>
            <SmartNudgesWidget />
          </div>
          
          {/* Nudge Types */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              Nudge Types
            </h2>
            <Card className="border-2 border-purple-200">
              <CardContent className="pt-4">
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-2">
                    <span className="text-xl">💧</span>
                    <div>
                      <p className="font-medium text-gray-900">Hydration</p>
                      <p className="text-xs text-gray-600">
                        Reminds to log water when not done by afternoon
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-2">
                    <span className="text-xl">🚶</span>
                    <div>
                      <p className="font-medium text-gray-900">Activity</p>
                      <p className="text-xs text-gray-600">
                        Suggests movement when inactive for the day
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-2">
                    <span className="text-xl">🌙</span>
                    <div>
                      <p className="font-medium text-gray-900">Sleep</p>
                      <p className="text-xs text-gray-600">
                        Encourages early rest after poor sleep pattern
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-2">
                    <span className="text-xl">🔥</span>
                    <div>
                      <p className="font-medium text-gray-900">Streaks</p>
                      <p className="text-xs text-gray-600">
                        Motivates to maintain active streaks
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-2">
                    <span className="text-xl">📝</span>
                    <div>
                      <p className="font-medium text-gray-900">Check-In</p>
                      <p className="text-xs text-gray-600">
                        Gentle reminder to complete daily loop
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Frequency Limits */}
        <Card className="border-2 border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="text-amber-900">Smart Frequency Limits</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Time Limits</h4>
                <ul className="space-y-1 text-gray-700">
                  <li>• Hydration: 4 hours between nudges</li>
                  <li>• Activity: 6 hours between nudges</li>
                  <li>• Sleep: 12 hours between nudges</li>
                  <li>• Check-in: 8 hours between nudges</li>
                  <li>• Streaks: 24 hours between nudges</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Smart Rules</h4>
                <ul className="space-y-1 text-gray-700">
                  <li>• No nudges during sleep (10pm-7am)</li>
                  <li>• Max 2 nudges shown at once</li>
                  <li>• Priority-based ordering</li>
                  <li>• Context-aware timing</li>
                  <li>• User can dismiss any nudge</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Example Messages */}
        <Card className="border-2 border-green-200">
          <CardHeader>
            <CardTitle className="text-green-900">Example Nudge Messages</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-gray-800">
                  💧 <strong>Stay Hydrated:</strong> "You haven't logged water today — a quick glass may help your energy"
                </p>
              </div>
              
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-gray-800">
                  🌙 <strong>Rest Earlier Tonight:</strong> "Your sleep has been low recently — try resting earlier tonight"
                </p>
              </div>
              
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-gray-800">
                  🚶 <strong>Time to Move:</strong> "You haven't moved much today — a short walk could boost your mood"
                </p>
              </div>
              
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-gray-800">
                  🔥 <strong>Protect Your Streak:</strong> "You're on a 5-day streak! Complete today's habits to keep it going"
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Back Button */}
        <div className="flex justify-center">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="outline"
            className="border-2 border-gray-300 hover:bg-gray-50"
          >
            Back to Home
          </Button>
        </div>
      </div>

      {/* Notification Variant (bottom-right) */}
      <SmartNudgesNotification />
    </div>
  );
};

export default SmartNudgesDemo;
